import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectAuthToken } from "@store/selectors/auth/auth.selectors";
import { MsalService } from '@azure/msal-angular';
import { environment } from '@environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly store = inject(Store);
  private readonly msalService = inject(MsalService);

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  intercept(req: HttpRequest<any>, handler: HttpHandler): Observable<HttpEvent<any>> {
    return this.store.select(selectAuthToken).pipe(
      take(1),
      switchMap((token: string) => {
        const authReq = this.addTokenToRequest(req, token);
        return handler.handle(authReq).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return this.handle401Error(req, handler, token);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private addTokenToRequest(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  private handle401Error(request: HttpRequest<any>, handler: HttpHandler, oldToken: string): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const account = this.msalService.instance.getActiveAccount();
      
      if (!account) {
        this.isRefreshing = false;
        return throwError(() => new Error('No active account for token refresh'));
      }

      // Force refresh the token
      return this.msalService.acquireTokenSilent({
        scopes: [`${environment.msalConfig.auth.apiScope}`],
        account: account,
        forceRefresh: true
      }).pipe(
        switchMap((tokenResponse) => {
          const newToken = tokenResponse.accessToken;
          this.refreshTokenSubject.next(newToken);
          
          // Retry the request with new token
          const retryReq = this.addTokenToRequest(request, newToken);
          return handler.handle(retryReq);
        }),
        catchError((error) => {
          console.error('Token refresh failed:', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      // Wait for the token refresh to complete
      return this.refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap(token => {
          const retryReq = this.addTokenToRequest(request, token);
          return handler.handle(retryReq);
        })
      );
    }
  }
}